/**
 * Upload API hooks + tus client helpers (Wave 2.23 FE, TASK-051a).
 *
 * The pipeline (admin-arch §5.1):
 *   1. `useInitUploadMutation()` → `POST /admin/upload/init` returns
 *      `{ upload_id, upload_url, expires_at }`. The schema lives in
 *      `@music-app/shared` (`uploadInitResponseSchema`).
 *   2. The caller hands the resulting `upload_url` to {@link createTusUpload},
 *      which boots a `tus-js-client` `Upload` instance pointed at the URL.
 *      The tus protocol resumes on transient network failures via
 *      `retryDelays`.
 *   3. On `onSuccess` the BE's tus finish hook (admin-arch §5.1) drives the
 *      transcoding pickup — the FE does NOT call the legacy
 *      `:upload_id/complete` endpoint, that route is reserved for QA scripts
 *      that bypass tus.
 *
 * Why the helper instead of constructing `tus.Upload` inline at the page:
 *   - centralised auth-header injection (Bearer token from the auth context
 *     getter passed by the caller — keeps this module decoupled from React),
 *   - sensible defaults for `chunkSize` and `retryDelays` (1s/3s/5s/10s, total
 *     ~19s before giving up; matches PRD §3.5 "загрузка прервана, продолжается
 *     автоматически"),
 *   - one place to evolve when websocket progress lands in a later wave.
 */

import { useMutation } from '@tanstack/react-query';
import * as tus from 'tus-js-client';
import {
  uploadInitResponseSchema,
  type UploadInit,
  type UploadInitResponse,
} from '@music-app/shared';
import { apiClient } from '@/lib/api-client';

/**
 * Default chunk size — 5 MiB matches the AWS S3 multipart minimum and the
 * `S3_MIN_PART_SIZE_BYTES` constant on the server side.
 */
export const TUS_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * `localStorage` key prefix `tus-js-client` uses for its fingerprint store
 * (see `tus.UrlStorage` defaults). Exposed so `clearTusUploadStorage()` can
 * wipe stale entries on logout without coupling other modules to the
 * library's internals.
 */
export const TUS_URL_STORAGE_PREFIX = 'tus::';

/**
 * Wipe every persisted tus upload URL from `localStorage`.
 *
 * Called on logout (auth-provider) so a fresh sign-in does not inherit
 * pending uploads from the previous user — the original concern that led
 * Wave 2.23 to disable the URL store entirely. Resume-after-refresh
 * (TASK-051a-followup) re-enables the store for the live session and we
 * pay the cleanup cost only at the session boundary.
 */
export function clearTusUploadStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    const store = window.localStorage;
    const toRemove: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(TUS_URL_STORAGE_PREFIX)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) store.removeItem(key);
  } catch {
    // localStorage may be disabled (private mode / quota). Cleanup is best
    // effort — stale entries are rejected by the BE on first PATCH anyway.
  }
}

/**
 * Retry schedule for transient network failures (ms). Caps total wait at
 * ~19 s before surfacing the error — long enough to survive a network
 * blip, short enough to avoid a stuck UI.
 */
export const TUS_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 5000, 10_000];

/**
 * Strip the pre-allocated `<32-hex>` upload id from a tus `upload_url` so the
 * client targets the bare tus collection endpoint (BUG-063), while preserving
 * the trailing slash that NestJS's `@All('tus/*')` requires to match
 * (BUG-069).
 *
 * tus-js-client v4 POSTs to the configured `endpoint` verbatim. The NestJS
 * route pattern `tus/*` only matches paths that include the `tus/` segment
 * with at least an empty `*` after it — `…/admin/upload/tus` (no slash) falls
 * through to the global 404 handler. By stripping only the hex (not the slash
 * before it), we end up at `…/admin/upload/tus/`, which `tus/*` matches as
 * `tus/` with an empty wildcard.
 *
 * If the input lacks a trailing hex id, the URL is returned unchanged — the
 * caller is then responsible for any further adjustment.
 */
export function deriveTusEndpoint(uploadUrl: string): string {
  return uploadUrl.replace(/[a-f0-9]{32}\/?$/, '') || uploadUrl;
}

export function useInitUploadMutation() {
  return useMutation<UploadInitResponse, Error, UploadInit>({
    mutationFn: async (payload) => {
      const raw = await apiClient<unknown>('/admin/upload/init', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return uploadInitResponseSchema.parse(raw);
    },
  });
}

/**
 * BUG-073 — after the tus client signals `onSuccess`, the BE's tus finish
 * hook still has to mint the `content.tracks` row inside its own
 * transaction. Until then `related_track_id` is NULL. This helper hits
 * `POST /admin/upload/:upload_id/complete` (a thin echo endpoint that
 * does NOT re-run sha256/dup logic — see `UploadController.complete`)
 * to read the freshest row, and the caller polls with a short backoff
 * until either the track id appears or the budget expires.
 */
export async function pollUploadTrackId(
  uploadIdHex: string,
  options: { attempts?: number; intervalMs?: number } = {},
): Promise<string | null> {
  const attempts = options.attempts ?? 8;
  const intervalMs = options.intervalMs ?? 750;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const raw = await apiClient<{ track_id: string | null }>(
        `/admin/upload/${encodeURIComponent(uploadIdHex)}/complete`,
        { method: 'POST' },
      );
      if (raw.track_id) return raw.track_id;
    } catch {
      // Network blip / transient 5xx — retry until the budget expires.
    }
    if (i < attempts - 1) {
      await new Promise<void>((resolve) => setTimeout(resolve, intervalMs));
    }
  }
  return null;
}

export interface CreateTusUploadOptions {
  /** File the user selected (must satisfy size limits enforced upstream). */
  file: File;
  /** `upload_url` from the `POST /admin/upload/init` response. */
  uploadUrl: string;
  /**
   * Pre-allocated `upload_id` (32 hex chars) from `POST /admin/upload/init`.
   * Carried into the tus `Upload-Metadata` header as `upload_id_hex` so the
   * server's `namingFunction` reuses our DB row instead of minting its own
   * id on POST creation (BUG-063).
   */
  uploadIdHex: string;
  /** Returns the in-memory access token (null while bootstrapping). */
  getAuthToken: () => string | null;
  /** Bytes uploaded so far / bytes total. Fires per chunk. */
  onProgress: (bytesSent: number, bytesTotal: number) => void;
  /** Tus has confirmed all bytes are stored. */
  onSuccess: () => void;
  /** Terminal failure after retries exhausted. */
  onError: (error: Error) => void;
}

/**
 * Wires a {@link tus.Upload} to the BE multipart surface and returns the
 * instance so the caller may `start()` / `abort()` it from React handlers.
 *
 * Note on auth: `tus-js-client` calls `headers` lazily on each request, but
 * the public API only accepts a plain `Record<string, string>`. The caller is
 * expected to call this helper *after* a successful `/admin/upload/init` —
 * the access token is read once at that boundary. If the token rotates
 * mid-upload, the `onAfterResponse` hook below would be the natural place to
 * intercept 401s and re-create the upload; we defer that until the auth team
 * publishes a token-rotation event contract.
 */
export function createTusUpload(options: CreateTusUploadOptions): tus.Upload {
  const {
    file,
    uploadUrl,
    uploadIdHex,
    getAuthToken,
    onProgress,
    onSuccess,
    onError,
  } = options;

  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // BUG-063 / BUG-069: tus-js-client treats `uploadUrl` as a resume target
  // and starts with a HEAD that 404s (no S3 metadata yet — init only created
  // the DB row). Switch to `endpoint`-mode by stripping the trailing hex id
  // while keeping the slash before it so NestJS's `@All('tus/*')` matches.
  // See `deriveTusEndpoint` for the routing rationale.
  const endpoint = deriveTusEndpoint(uploadUrl);

  return new tus.Upload(file, {
    endpoint,
    chunkSize: TUS_CHUNK_SIZE_BYTES,
    retryDelays: [...TUS_RETRY_DELAYS_MS],
    headers,
    // Wave 2.66 FE (TASK-051a-followup): re-enable the fingerprint store so a
    // page refresh / browser crash mid-upload picks up where it left off.
    // The original Wave 2.23 concern (stale URL surviving logout) is now
    // addressed by `clearTusUploadStorage()` wired into auth-provider —
    // every logout wipes the `tus::*` keys before a new sign-in can inherit
    // them. We additionally clear on success so completed uploads do not
    // accumulate forever.
    storeFingerprintForResuming: true,
    removeFingerprintOnSuccess: true,
    metadata: {
      filename: file.name,
      filetype: file.type,
      upload_id_hex: uploadIdHex,
    },
    onProgress,
    onSuccess,
    onError: (err) => {
      onError(err instanceof Error ? err : new Error(String(err)));
    },
  });
}
